import { DataTable } from '@components/DataTable';
import { useContainerHeight } from '@components/DataTable/hooks';
import { DataTableColumn, SelectionModel } from '@components/DataTable/types';
import { Avatar, Box, Chip, CircularProgress, IconButton, Stack, Typography } from '@mui/material';
import { Roles } from '@utils/roleUtils';
import moment from 'moment';
import { useCallback, useMemo } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { PopUpMenu } from '../components/PopUpMenu';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface OrganizationUserTableItem {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
    role: string;
    isMemberActive: boolean;
    active?: boolean;
    createdDate?: string;
    color?: string;
    file?: { url?: string };
    subordinates?: any[];
    totalSubordinates?: number;
    jobOfferStatsOrgUser?: { jobs?: { total?: number } };
}

export interface OrganizationUsersDataTableProps {
    users: OrganizationUserTableItem[];
    totalDocs: number;
    loading: boolean;
    isActionLoading?: boolean;
    page: number;
    rowsPerPage: number;
    selectionModel: SelectionModel;
    onPageChange: (page: number) => void;
    onPageSizeChange: (size: number) => void;
    onSelectionModelChange: (model: SelectionModel) => void;
    onEdit: (user: OrganizationUserTableItem) => void;
    onDelete: (id: string) => void;
    onResendInvitation: (id: string) => void;
    onUpdateStatus: (user: OrganizationUserTableItem) => void;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const roleLabels: Record<string, string> = {
    [Roles.ADMIN_PRO]: 'Admin',
    [Roles.MEMBER_PRO]: 'Membre',
    [Roles.ACCOUNTANT_PRO]: 'Comptable',
};

const roleColors: Record<string, 'primary' | 'secondary' | 'warning'> = {
    [Roles.ADMIN_PRO]: 'primary',
    [Roles.MEMBER_PRO]: 'secondary',
    [Roles.ACCOUNTANT_PRO]: 'warning',
};

// ─── Component ────────────────────────────────────────────────────────────────

const OrganizationUsersDataTable = ({
    users,
    totalDocs,
    loading,
    isActionLoading,
    page,
    rowsPerPage,
    selectionModel,
    onPageChange,
    onPageSizeChange,
    onSelectionModelChange,
    onEdit,
    onDelete,
    onResendInvitation,
    onUpdateStatus,
}: OrganizationUsersDataTableProps) => {
    const { id: companyId } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { containerRef, containerHeight } = useContainerHeight();

    const columns = useMemo<DataTableColumn<OrganizationUserTableItem>[]>(
        () => [
            {
                id: 'name',
                label: 'Nom',
                sticky: true,
                wrap: true,
                minWidth: 200,
                renderCell: ({ row }) => (
                    <Stack direction="row" alignItems="center" gap={1.5}>
                        <Avatar
                            src={row.file?.url}
                            sx={{ bgcolor: row.color, width: 36, height: 36, fontSize: 13, fontWeight: 700 }}>
                            {`${row.firstName?.[0] ?? ''}${row.lastName?.[0] ?? ''}`.toUpperCase()}
                        </Avatar>
                        <Stack sx={{ minWidth: 0 }}>
                            <Typography
                                variant="body2"
                                component={row.role === Roles.ADMIN_PRO ? Link : Link}
                                to={
                                    row.role === Roles.ADMIN_PRO
                                        ? `/companies/${companyId}/users/${row._id}`
                                        : `/companies/${companyId}/users/${row._id}/edit`
                                }
                                sx={{
                                    fontWeight: 600,
                                    color: 'inherit',
                                    textDecoration: 'none',
                                    '&:hover': { textDecoration: 'underline' },
                                }}>
                                {`${row.firstName ?? ''} ${row.lastName ?? ''}`}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" noWrap>
                                {row.email}
                            </Typography>
                        </Stack>
                    </Stack>
                ),
            },
            {
                id: 'role',
                label: 'Rôle',
                minWidth: 100,
                renderCell: ({ row }) => (
                    <Chip
                        label={roleLabels[row.role] ?? row.role}
                        color={roleColors[row.role] ?? 'default'}
                        size="small"
                    />
                ),
            },
            {
                id: 'status',
                label: 'Statut',
                minWidth: 110,
                renderCell: ({ row }) => (
                    <Chip
                        label={row.isMemberActive ? 'Actif' : 'Désactivé'}
                        color={row.isMemberActive ? 'success' : 'error'}
                        size="small"
                    />
                ),
            },
            {
                id: 'createdDate',
                label: "Date d'ajout",
                minWidth: 120,
                renderCell: ({ row }) =>
                    row.createdDate ? (
                        <Typography variant="body2">
                            {moment(row.createdDate).format('DD MMM YYYY')}
                        </Typography>
                    ) : (
                        '-'
                    ),
            },
            {
                id: 'jobOffers',
                label: "Offres d'emploi",
                minWidth: 120,
                align: 'center',
                renderCell: ({ row }) => (
                    <Typography
                        component={Link}
                        to={`/companies/${companyId}/job-offers?assigneeId=${row._id}`}
                        variant="body2"
                        sx={{
                            fontWeight: 600,
                            color: 'primary.main',
                            textDecoration: 'none',
                            '&:hover': { textDecoration: 'underline' },
                        }}>
                        {row.jobOfferStatsOrgUser?.jobs?.total ?? 0}
                    </Typography>
                ),
            },
            {
                id: 'actions',
                label: 'Actions',
                align: 'center',
                width: 80,
                renderCell: ({ row }) => (
                    <>
                        {isActionLoading ? (
                            <Stack alignItems="center" justifyContent="center" width={24}>
                                <CircularProgress size={20} color="secondary" />
                            </Stack>
                        ) : (
                            <PopUpMenu
                                key={row._id}
                                id={row._id}
                                button={<i className="fi fi-br-menu-dots-vertical" style={{ fontSize: 14 }} />}
                                editHref={`/companies/${companyId}/users/${row._id}/edit`}
                                onDelete={() => onDelete(row._id)}
                                onResendInvitation={() => onResendInvitation(row._id)}
                                onUpdateStatus={() => onUpdateStatus(row)}
                                labelActive={row.isMemberActive ? 'Désactiver' : 'Activer'}
                            />
                        )}
                    </>
                ),
            },
        ],
        [isActionLoading, onDelete, onResendInvitation, onUpdateStatus, companyId],
    );

    return (
        <Box ref={containerRef} sx={{ flex: 1, minHeight: 0, width: '100%' }}>
            <DataTable
                stickyHeader
                rows={users}
                columns={columns}
                loading={loading}
                checkboxSelection
                selectionModel={selectionModel}
                onSelectionModelChange={onSelectionModelChange}
                height={containerHeight}
                pagination={{
                    currentPage: page,
                    pageSize: rowsPerPage,
                    totalRows: totalDocs,
                    onPageChange,
                    onPageSizeChange,
                    pageSizeOptions: [10, 20, 50],
                }}
            />
        </Box>
    );
};

export default OrganizationUsersDataTable;
